import axios from 'axios';
import { 
  validateInput, 
  checkRateLimit, 
  getSecureHeaders, 
  logSecurityEvent,
  detectSuspiciousInput 
} from './security';
import { storageSync } from './storage';
import { apiBaseURL } from '../constants/routes';
import { describeApiError, isSessionExpired } from '@tuneswell/shared/apiErrors';

const getAuthToken = () => storageSync.getItem("token");

// Previously, detecting an expired/invalid token here only cleared the
// stored token and threw an error back to whichever query/mutation called
// it - nothing else reacted, so the rest of the app (AuthContext's
// isAuthed, React Query's cached data) stayed exactly as it was. The user
// was left looking at a half-working page (some sections still showing
// stale cached data, the one that just 401'd silently broken) with no
// indication why, until a manual refresh happened to re-run the app's
// bootstrap auth check against the now-empty token. Dispatching this event
// lets AuthProvider react immediately: clear all auth state and the query
// cache, and redirect away from whatever protected route the user is on -
// not just a few requests from now, right when it actually happens.
const notifySessionExpired = () => {
  try {
    sessionStorage.setItem('sessionExpiredMessage', 'Your session has expired. Please log in again.');
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  } catch (e) {
    // sessionStorage/CustomEvent unavailable (e.g. some test environments) -
    // not worth failing the original request over.
  }
};


// The label or publisher picked in the header's switcher, so pages that
// act on "your label" act on that one (see utils/auth.js, workingAs)
export const actingAsHeaders = () => {
  try {
    const workingAs = JSON.parse(sessionStorage.getItem('tuneswell:listening-mode'))?.workingAs;
    return workingAs?.kind && workingAs?.id ? { 'X-Acting-As': `${workingAs.kind}:${workingAs.id}` } : {};
  } catch {
    return {};
  }
};

const getConfig = () => {
  const authToken = getAuthToken();
  return {
    headers: {
      ...getSecureHeaders(),
      ...(authToken ? { "Authorization": `Token ${authToken}` } : {}),
      ...actingAsHeaders(),
    },
    timeout: 30000, // 30 second timeout for API requests
  };
};

export const fetcher = async (url) =>{
  try {
    // Security: Validate and sanitize URL input
    const urlValidation = validateInput(url, 'url');
    if (!urlValidation.isValid) {
      logSecurityEvent('INVALID_URL', { url, error: urlValidation.error });
      throw new Error(`Invalid URL: ${urlValidation.error}`);
    }
    
    // Security: Check for suspicious patterns in URL path only (not query params)
    // Parse URL to check only the pathname, not query parameters
    // Also allow known API endpoints to avoid false positives
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      // Allow known API endpoints (paths starting with /api/)
      const isKnownAPIEndpoint = pathname.startsWith('/api/') || url.startsWith(apiBaseURL);
      
      if (!isKnownAPIEndpoint) {
        // Only check the pathname for suspicious patterns, not query params
        if (detectSuspiciousInput(pathname)) {
          logSecurityEvent('SUSPICIOUS_URL', { url, pathname });
          throw new Error('Suspicious URL pattern detected');
        }
      }
    } catch (urlParseError) {
      // If URL parsing fails, check if it's a known API endpoint
      const isKnownAPIEndpoint = url.startsWith(apiBaseURL) || url.startsWith('/api/');
      
      if (!isKnownAPIEndpoint) {
        // Fall back to checking the full URL for relative URLs
        if (detectSuspiciousInput(url)) {
          logSecurityEvent('SUSPICIOUS_URL', { url });
          throw new Error('Suspicious URL pattern detected');
        }
      }
    }
    
    // Security: Rate limiting
    const userKey = getAuthToken() || 'anonymous';
    if (!checkRateLimit(userKey, 100, 60000)) { // 100 requests per minute
      logSecurityEvent('RATE_LIMIT_EXCEEDED', { userKey, url });
      throw new Error('Rate limit exceeded. Please try again later.');
    }
    
    // Extend timeout for album/track-related endpoints that might take longer
    let config = getConfig();
    if (url.includes('/music/album-tracks/') || url.includes('/music/album/') || url.includes('/music/track/')) {
      config = {
        ...config,
        timeout: 120000 // 2 minutes for album/track endpoints
      };
    }
    
    const response = await axios.get(url, config);
    return response.data;
  } catch (error) {
    // Silently handle 404 errors (endpoints that don't exist yet)
    if (error.response?.status === 404) {
      // Don't log 404 errors - they're expected for optional endpoints
      throw error; // Still throw so caller can handle it
    }
    
    console.error('Fetcher: Request failed:', error);
    console.error('Fetcher: Error details:', {
      url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code,
    });
    
    if (isSessionExpired(error)) {
      storageSync.removeItem('token');
      notifySessionExpired();
    }
    throw new Error(describeApiError(error));
  }
}

export const mutation = async (url, data, method = 'POST', onUploadProgress = null) =>{
  try {
    // Security: Validate URL
    const urlValidation = validateInput(url, 'url');
    if (!urlValidation.isValid) {
      logSecurityEvent('INVALID_URL', { url, error: urlValidation.error });
      throw new Error(`Invalid URL: ${urlValidation.error}`);
    }
    
    // Security: Check for suspicious patterns in URL path only (not query params)
    // Allow known API endpoints to avoid false positives
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      // Allow known API endpoints (paths starting with /api/)
      const isKnownAPIEndpoint = pathname.startsWith('/api/') || url.startsWith(apiBaseURL);
      
      if (!isKnownAPIEndpoint) {
        // Only check the pathname for suspicious patterns, not query params
        if (detectSuspiciousInput(pathname)) {
          logSecurityEvent('SUSPICIOUS_URL', { url, pathname });
          throw new Error('Suspicious URL pattern detected');
        }
      }
    } catch (urlParseError) {
      // If URL parsing fails, check if it's a known API endpoint
      const isKnownAPIEndpoint = url.startsWith(apiBaseURL) || url.startsWith('/api/');
      
      if (!isKnownAPIEndpoint) {
        // Fall back to checking the full URL for relative URLs
        if (detectSuspiciousInput(url)) {
          logSecurityEvent('SUSPICIOUS_URL', { url });
          throw new Error('Suspicious URL pattern detected');
        }
      }
    }
    
    // Security: Validate and sanitize data
    if (data && typeof data === 'object') {
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string') {
          // Check for suspicious patterns in string values
          if (detectSuspiciousInput(value)) {
            logSecurityEvent('SUSPICIOUS_INPUT', { field: key, value });
            throw new Error(`Suspicious input detected in field: ${key}`);
          }
        }
      }
    }
    
    // Security: Rate limiting for mutations
    // Very permissive client-side rate limiting (200 per 15 minutes) to catch only extreme abuse
    // This allows power users to work quickly while still providing a safety net
    // Server-side protection (Nginx 20 req/sec, authentication, validation) provides primary defense
    // OWASP recommends server-side rate limiting, which we have via Nginx and will add to endpoints
    const userKey = getAuthToken() || 'anonymous';
    if (!checkRateLimit(userKey, 200, 900000)) { // 200 mutations per 15 minutes
      logSecurityEvent('MUTATION_RATE_LIMIT_EXCEEDED', { userKey, url, method });
      throw new Error('Rate limit exceeded. Please try again later.');
    }
    
    let response;
    const baseConfig = getConfig();
    let config = baseConfig;
    
    // Determine timeout based on endpoint (applies to all requests, not just FormData)
    // Album/track creation should be reasonably fast - if it takes longer, there's a problem
    // Files are pre-uploaded, so this is just metadata + database operations
    let timeout = baseConfig.timeout; // Default from baseConfig (30 seconds)
    
    if (url.includes('/music/album/create/')) {
      // Album creation: even with 20+ tracks, should complete in 15 minutes
      // If files are pre-uploaded, this is just DB operations and file copying
      // Large albums with many tracks may take longer for processing
      timeout = 900000; // 15 minutes
    } else if (url.includes('/music/track/create/')) {
      // Single track creation: should be very fast
      timeout = 120000; // 2 minutes
    } else if (data instanceof FormData) {
      // Other FormData uploads (like temporary uploads) - actual file upload
      timeout = 600000; // 10 minutes for actual file uploads
    } else if (method === 'DELETE' && url.includes('/music/temporary-upload/')) {
      // DELETE requests for temporary uploads may need to delete large files
      // Give them more time than the default 30 seconds
      timeout = 120000; // 2 minutes for deleting temporary uploads
    }
    
    // Handle FormData differently - don't set Content-Type for multipart/form-data
    if (data instanceof FormData) {
      // Create a new config object with extended timeout for file uploads
      config = {
        ...baseConfig,
        timeout: timeout,
        headers: {
          ...baseConfig.headers,
          'Content-Type': undefined // Let axios set it automatically with boundary
        }
      };
      
      // Remove Content-Type header to let axios set it automatically with boundary
      delete config.headers['Content-type'];
      
      // Add upload progress tracking
      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress;
      }
    } else if (timeout !== baseConfig.timeout) {
      // Apply extended timeout even for non-FormData requests to album/track creation endpoints
      config = {
        ...baseConfig,
        timeout: timeout
      };
    }
    
    if (method === 'DELETE') {
      // For DELETE requests, send data in the request body
      response = await axios({
        method: 'DELETE',
        url: url,
        data: data,
        ...config
      });
    } else if (method === 'PUT') {
      response = await axios.put(url, data, config);
    } else if (method === 'PATCH') {
      response = await axios.patch(url, data, config);
    } else {
      response = await axios.post(url, data, config);
    }
    
    return (response.data);
  } catch (error){
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error Details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method
      });
    }
    if (isSessionExpired(error)) {
      storageSync.removeItem('token');
      notifySessionExpired();
    }
    // Shared with the phone app (packages/shared/apiErrors.js)
    throw new Error(describeApiError(error));
  }
}